import React, { useEffect, useMemo, useRef, useState } from "react";

const COLOURS = [
    "#EF4444",
    "#F65E2E",
    "#F97316",
    "#F59E0B",
    "#EAB308",
    "#84CC16",
    "#22C55E",
    "#1FCF8A",
    "#14B8A6",
    "#0EA5E9",
    "#3B82F6",
    "#6366F1",
    "#8B5CF6",
    "#A855F7",
    "#EC4899",
    "#F43F5E",
    "#FB7185",
    "#10B981",
    "#34D399",
    "#6366F1",
    "#818CF8",
    "#F59E0B",
    "#FBBF24",
    "#F87171",
];


function pickWeightedIndex(items) {
    const weights = items.map((item) => item.weight ?? 10);
    const total = weights.reduce((sum, weight) => sum + weight, 0);

    let random = Math.random() * total;

    for (let index = 0; index < weights.length; index += 1) {
        random -= weights[index];

        if (random <= 0) {
            return index;
        }
    }

    return weights.length - 1;
}

function normalizeDegrees(degrees) {
    return ((degrees % 360) + 360) % 360;
}

function buildSegments(items) {
    const totalWeight = items.reduce((sum, item) => sum + (item.weight ?? 10), 0);

    let currentAngle = 0;

    return items.map((item, index) => {
        const weight = item.weight ?? 10;
        const sweep = (weight / totalWeight) * 360;

        const segment = {
            item,
            index,
            colour: COLOURS[index % COLOURS.length],
            startDeg: currentAngle,
            endDeg: currentAngle + sweep,
            midDeg: currentAngle + sweep / 2,
            sweep,
        };

        currentAngle += sweep;

        return segment;
    });
}

function getPubKey(pub, index) {
    return pub.id ?? pub.name ?? index;
}

function easeOutCubic(value) {
    return 1 - Math.pow(1 - value, 3);
}

function useAnimatedPubs(pubs, duration = 800) {
    const [animatedPubs, setAnimatedPubs] = useState(pubs);

    const animatedPubsRef = useRef(pubs);
    const frameRef = useRef(null);
    const latestPubsRef = useRef(pubs);

    latestPubsRef.current = pubs;

    const targetSignature = useMemo(
        () =>
            pubs
                .map(
                    (pub, index) =>
                        `${getPubKey(pub, index)}:${pub.weight ?? 10}`
                )
                .join("|"),
        [pubs]
    );

    useEffect(() => {
        window.cancelAnimationFrame(frameRef.current);

        const targetPubs = latestPubsRef.current;
        const safeDuration = Math.max(duration, 1);

        const startWeightsByKey = new Map(
            animatedPubsRef.current.map((pub, index) => [
                getPubKey(pub, index),
                pub.weight ?? 10,
            ])
        );

        const startedAt = performance.now();

        function animate(now) {
            const progress = Math.min((now - startedAt) / safeDuration, 1);
            const easedProgress = easeOutCubic(progress);

            const nextPubs = targetPubs.map((pub, index) => {
                const key = getPubKey(pub, index);
                const targetWeight = pub.weight ?? 10;
                const startWeight =
                    startWeightsByKey.get(key) ?? targetWeight;

                return {
                    ...pub,
                    weight:
                        startWeight +
                        (targetWeight - startWeight) * easedProgress,
                };
            });

            animatedPubsRef.current = nextPubs;
            setAnimatedPubs(nextPubs);

            if (progress < 1) {
                frameRef.current = window.requestAnimationFrame(animate);
            }
        }

        frameRef.current = window.requestAnimationFrame(animate);

        return () => {
            window.cancelAnimationFrame(frameRef.current);
        };
    }, [targetSignature, duration]);

    return animatedPubs;
}

export default function CssWheel({ pubs = [], onResult }) {
    const [rotation, setRotation] = useState(0);
    const [isSpinning, setIsSpinning] = useState(false);
    const [winner, setWinner] = useState(null);

    const spinTimeoutRef = useRef(null);

    const animatedPubs = useAnimatedPubs(pubs);
    const segments = useMemo(() => buildSegments(animatedPubs), [animatedPubs]);

    const wheelBackground = useMemo(() => {
        if (!segments.length) {
            return "#E5E7EB";
        }

        const gradientStops = segments
            .map(
                (segment) =>
                    `${segment.colour} ${segment.startDeg}deg ${segment.endDeg}deg`
            )
            .join(", ");

        return `conic-gradient(from 90deg, ${gradientStops})`;
    }, [segments]);

    function spinWheel() {
        if (isSpinning || !segments.length) {
            return;
        }

        window.clearTimeout(spinTimeoutRef.current);

        const winnerIndex = pickWeightedIndex(animatedPubs);
        const selectedSegment = segments[winnerIndex];

        /**
         * Pointer is fixed on the right side.
         * To land the selected segment under the pointer:
         * final wheel rotation should place selected mid angle at 0deg.
         */
        const targetRemainder = normalizeDegrees(-selectedSegment.midDeg);
        const currentRemainder = normalizeDegrees(rotation);

        let delta = targetRemainder - currentRemainder;

        if (delta < 0) {
            delta += 360;
        }

        const fullSpins = 6;
        const finalRotation = rotation + fullSpins * 360 + delta;

        setIsSpinning(true);
        setWinner(null);
        setRotation(finalRotation);

        spinTimeoutRef.current = window.setTimeout(() => {
            setWinner(selectedSegment.item);
            setIsSpinning(false);
            onResult?.(selectedSegment.item);
        }, 5200);
    }

    if (!pubs.length) {
        return (
            <div className="flex min-h-[20rem] items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
                Add some options to spin the wheel.
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center gap-6 w-full">
            <div className="relative w-full sm:w-auto lg:w-1/3 aspect-square">
                <div className="absolute inset-0 rounded-full shadow-2xl shadow-slate-900/20">
                    <div
                        className="relative h-full w-full overflow-hidden rounded-full border-[10px] border-white shadow-inner transition-transform duration-[5200ms] ease-out"
                        style={{
                            background: wheelBackground,
                            transform: `rotate(${rotation}deg)`,
                        }}
                    >
                        {segments.map((segment) => {
                            return (
                                <div
                                    key={`${segment.item.name}-${segment.index}`}
                                    className="absolute left-1/2 top-1/2 z-10 flex w-1/2 origin-left items-center"
                                    style={{
                                        transform: `rotate(${segment.midDeg}deg)`,
                                    }}
                                >
                                    <div
                                        className="flex w-full h-[1px] items-center justify-end text-right mr-5 font-black uppercase leading-tight tracking-wide text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.55)]"
                                        style={{
                                            fontSize:
                                                segment.sweep < 12
                                                    ? "clamp(0.45rem, 1.2vw, 0.7rem)"
                                                    : segment.sweep < 20
                                                        ? "clamp(0.5rem, 1.5vw, 0.8rem)"
                                                        : "clamp(0.55rem, 2vw, 0.95rem)",
                                        }}
                                    >
                                        <span className="line-clamp-2">
                                            {segment.item.name}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}

                    </div>
                </div>

                <div className="pointer-events-none absolute -right-5 top-1/2 z-30 -translate-y-1/2 drop-shadow-lg">
                    <svg
                        width="44"
                        height="44"
                        viewBox="0 0 44 44"
                        className="block"
                        aria-hidden="true"
                    >
                        <polygon
                            points="40,4 4,22 40,40"
                            fill="#020617"
                            stroke="white"
                            strokeWidth="4"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>

                <button
                    onClick={spinWheel}
                    disabled={isSpinning}
                    className="absolute left-1/2 top-1/2 z-20 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-4 border-white bg-slate-950 text-xs font-black uppercase tracking-widest text-white shadow-xl"
                >
                    Spin
                </button>
            </div>               

            <button
                onClick={spinWheel}
                disabled={isSpinning}
                className={`px-8 py-3 rounded-full text-white font-bold text-lg shadow-lg transition-all
          ${isSpinning ? "bg-gray-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700 active:scale-95"}`}
            >
                {isSpinning ? "Spinning..." : "Spin the wheel"}
            </button>

            {winner && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-6 py-4 text-center shadow-sm">
                    <p className="text-xs font-bold uppercase tracking-widest text-emerald-600">
                        Winner
                    </p>
                    <p className="mt-1 text-2xl font-black text-emerald-950">
                        {winner.name}
                    </p>
                </div>
            )}
        </div>
    );
}